import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import agent from "../../api/agent";
import {
  ApiResponse,
  ArrayDataResponse,
  ClientUIResponse,
  DriverSelectedEmailUIResponse,
  FdpDriverAccountUIResponse,
  FdpToParcelCenterMappingResponse,
  FdpUIResponse,
  SingleFdpResponse,
  UserUIResponse,
} from "../../models/ApiResponse";
import {
  AddFdpRequest,
  BulkFdpRequest,
  DriverClientEmailRequest,
  updateFdpRequest,
} from "../../models/requestModels/fdpRequest";
import { Client } from "../../models/responseModels/clientsResponse";
import { FdpResponse } from "../../models/responseModels/FdpResponse";
import { FdpDriverClientAccount } from "../../models/responseModels/loginResponse";
import { ParcelCenterFdpMappingDto } from "../../models/responseModels/ParcelCenterFdpMappingDto";
import { UserResponse } from "../../models/responseModels/userResponse";
import { UpdateFdpInvoiceRequest } from "../../models/requestModels/RtsRequestAdminPanel";
import { RootState } from "../store/store";

type InitialState = {
  isFdpLoading: boolean;
  fdpList: FdpResponse[];
  fdpParcelCenterMappingList: ParcelCenterFdpMappingDto[];
  fdpListByClient: FdpResponse[];
  fdpUsersList: UserResponse[];
  fdpDriverSelectedEmailValue: string | null;
  fdpAssociatedClients: Client[];
  isDriverEmailLoading: boolean;
  errorMessage: string | null;
  fdcDriverAllClientEmails: FdpDriverClientAccount[];
  globallySelectedDriverClientEmail: FdpDriverClientAccount | null;
  driverUserCredsMapping: FdpDriverClientAccount[];
  selectedFDP: string | null;
};

const initialState: InitialState = {
  isFdpLoading: false,
  fdpList: [],
  fdpParcelCenterMappingList: [],
  fdpListByClient: [],
  fdpUsersList: [],
  fdpDriverSelectedEmailValue: null,
  fdpAssociatedClients: [],
  isDriverEmailLoading: false,
  errorMessage: null,
  fdcDriverAllClientEmails: [],
  globallySelectedDriverClientEmail: null,
  driverUserCredsMapping: [],
  selectedFDP: null
};

export const getfdpList = createAsyncThunk<FdpUIResponse>(
  "fdp/all",
  async () => {
    const response = await agent.FdpAgent.getFdpsList();
    return response;
  }
);

export const getfdpToParcelCenterMappingList =
  createAsyncThunk<FdpToParcelCenterMappingResponse>(
    "fdp/all-fdp-parcel-center-mappings",
    async () => {
      const response = await agent.FdpAgent.getFdpsToParcelCenterMappingList();
      return response;
    }
  );

export const getFdpListByClient = createAsyncThunk<FdpUIResponse, number>(
  "fdp/client",
  async (id: number) => {
    const response = await agent.FdpAgent.getFdpListByClient(id);
    return response;
  }
);

export const getfdpUsersList = createAsyncThunk<UserUIResponse, number>(
  "fdp/users",
  async (fdpId: number) => {
    const response = await agent.FdpAgent.getFdpUsersList(fdpId);
    return response;
  }
);

export const addNewfdp = createAsyncThunk<ApiResponse, AddFdpRequest>(
  "fdp/add",
  async (addFdpRequest: AddFdpRequest) => {
    const response = await agent.FdpAgent.addFdp(addFdpRequest);
    return response;
  }
);

export const addNewfdpInBulk = createAsyncThunk<
  ArrayDataResponse,
  BulkFdpRequest
>("fdp//bulk/add", async (fdps: BulkFdpRequest) => {
  const response = await agent.FdpAgent.addBulkFdps(fdps);
  return response;
});

export const updateFdpStatus = createAsyncThunk<
  ApiResponse,
  { fdpIds: string[]; activeStatus: boolean }
>("update/fdp/status", async ({ fdpIds, activeStatus }) => {
  const response = await agent.FdpAgent.updateFdpStatus(fdpIds, activeStatus);
  return response;
});

export const getFdpList = createAsyncThunk<FdpUIResponse>(
  "fdp/all-fdp",
  async () => {
    const response = await agent.FdpAgent.getFdpList();
    return response;
  }
);

export const updateFdp = createAsyncThunk<ApiResponse, updateFdpRequest>(
  "update/fdp",
  async (fdpUpdateRequest: updateFdpRequest) => {
    const response = await agent.FdpAgent.updateFdp(fdpUpdateRequest);
    return response;
  }
);

export const getfdpDriverClientEmails =
  createAsyncThunk<FdpDriverAccountUIResponse>(
    "fdp/clients/driver/emails",
    async () => {
      const response = await agent.FdpAgent.getFdpDriverClientEmails();
      return response;
    }
  );

export const fetchFdpDriverCredsMappingWithClientEmails =
  createAsyncThunk<FdpDriverAccountUIResponse>(
    "fdp/clients/driver/creds-mapping",
    async () => {
      const response = await agent.FdpAgent.getFdpDriverCredsMappingWithClientEmails();
      return response;
    }
  );
export const fetchFdpDriverSelectedEmail =
  createAsyncThunk<DriverSelectedEmailUIResponse>(
    "fdp/clients/driver/email",
    async () => {
      const response = await agent.FdpAgent.getFdpDriverSelectedEmail();
      return response;
    }
  );

export const updateRequestDriverEmail = createAsyncThunk<
  ApiResponse,
  { selectedAcc: FdpDriverClientAccount; comment: string }
>("update/fdp/clients/driver/acc", async ({ selectedAcc, comment }) => {
  const response = await agent.FdpAgent.updateFdpDriverSelectedAcc(
    selectedAcc,
    comment
  );
  return response;
});

export const removeFdpDriverAccount = createAsyncThunk<ApiResponse, FdpDriverClientAccount>(
  "remove/fdp/clients/driver/acc",
  async (toBeRemovedAccount: FdpDriverClientAccount) => {
    const response = await agent.FdpAgent.removeFdpDriverSelectedAcc(
      toBeRemovedAccount
    );
    return response;
  }
);

export const fetchFdpAssociatedClients = createAsyncThunk<
  ClientUIResponse,
  number
>("fdp/associated/clients", async (fdpId: number) => {
  const response = await agent.FdpAgent.fetchFdpAssociatedClients(fdpId);
  return response;
});

export const addFdpDriverClientEmail = createAsyncThunk<
  ArrayDataResponse,
  DriverClientEmailRequest
>(
  "add/fdp/driverClient/email",
  async (addDriverEmailRequest: DriverClientEmailRequest) => {
    const response = await agent.FdpAgent.addFdpDriverClientEmail(
      addDriverEmailRequest
    );
    return response;
  }
);

export const deleteDriverClientEmail = createAsyncThunk<
  ArrayDataResponse,
  DriverClientEmailRequest
>(
  "delete/fdp/driverClient/email",
  async (deleteDriverEmailRequest: DriverClientEmailRequest) => {
    const response = await agent.FdpAgent.deleteFdpDriverClientEmail(
      deleteDriverEmailRequest
    );
    return response;
  }
);

export const updateInvoiceStatusByPayableDate = createAsyncThunk<ApiResponse, UpdateFdpInvoiceRequest>(
  "update/fdp/invoiceStatus/",
  async(updateFdpInvoiceRequest: UpdateFdpInvoiceRequest) => {
    const response = await agent.FdpAgent.updateInvoiceStatusByPayableDate(
      updateFdpInvoiceRequest
    )
    return response;
  }
)

const fdpSlice = createSlice({
  name: "fdp",
  initialState,
  reducers: {
    clearFdpUserList: (state, action) => {
      state.fdpUsersList = action.payload;
    },
    updateFdpDriverSelectedEmailValue: (state, action) => {
      state.globallySelectedDriverClientEmail = action.payload;
    },
    updateFdpAssociatedClients: (state, action) => {
      state.fdpAssociatedClients = action.payload;
    },
    updateGlobalSelectedDriverClientEmail: (state, action) => {
      state.globallySelectedDriverClientEmail = action.payload;
    },
    setSelectedFDP: (state, action) => {
      state.selectedFDP =  action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getfdpList.pending, (state) => {
        state.isFdpLoading = true;
      })
      .addCase(getfdpList.fulfilled, (state, action) => {
        state.isFdpLoading = false;
        if (action.payload.isSuccess) {
          let data = action.payload.data;
          data.forEach((e) => {
            e.value = "" + e.id;
            e.label = e.companyName;
          });
          state.fdpList = data;
        } else {
          state.fdpList = [];
        }
      })
      .addCase(getfdpList.rejected, (state, action) => {
        state.isFdpLoading = false;
        state.fdpList = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(getfdpToParcelCenterMappingList.pending, (state) => {
        state.isFdpLoading = true;
      })
      .addCase(getfdpToParcelCenterMappingList.fulfilled, (state, action) => {
        state.isFdpLoading = false;
        if (action.payload.isSuccess) {
          state.fdpParcelCenterMappingList = action.payload.data;
        } else {
          state.fdpParcelCenterMappingList = [];
        }
      })
      .addCase(getfdpToParcelCenterMappingList.rejected, (state, action) => {
        state.isFdpLoading = false;
        state.fdpParcelCenterMappingList = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(getfdpUsersList.pending, (state) => {
        state.isFdpLoading = true;
      })
      .addCase(getfdpUsersList.fulfilled, (state, action) => {
        state.isFdpLoading = false;
        if (action.payload.isSuccess) {
          state.fdpUsersList = action.payload.data;
        } else {
          state.fdpUsersList = [];
        }
      })
      .addCase(getfdpUsersList.rejected, (state, action) => {
        state.isFdpLoading = false;
        state.fdpUsersList = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(fetchFdpDriverSelectedEmail.pending, (state) => {
        state.isFdpLoading = true;
      })
      .addCase(fetchFdpDriverSelectedEmail.fulfilled, (state, action) => {
        state.isFdpLoading = false;
        if (action.payload.isSuccess) {
          state.globallySelectedDriverClientEmail = action.payload.data;
        }
      })
      .addCase(fetchFdpDriverSelectedEmail.rejected, (state, action) => {
        state.isFdpLoading = false;
        state.errorMessage = action.error.message!;
      })
      .addCase(fetchFdpDriverCredsMappingWithClientEmails.pending, (state) => {
        state.isFdpLoading = true;
      })
      .addCase(fetchFdpDriverCredsMappingWithClientEmails.fulfilled, (state, action) => {
        state.isFdpLoading = false;
        if (action.payload.isSuccess) {
          state.driverUserCredsMapping = action.payload.data;
        }
      })
      .addCase(fetchFdpDriverCredsMappingWithClientEmails.rejected, (state, action) => {
        state.isFdpLoading = false;
        state.errorMessage = action.error.message!;
      })
      .addCase(updateFdpStatus.pending, (state) => {
        state.isFdpLoading = true;
      })
      .addCase(updateFdpStatus.fulfilled, (state) => {
        state.isFdpLoading = false;
      })
      .addCase(updateFdpStatus.rejected, (state) => {
        state.isFdpLoading = false;
      })
      .addCase(updateFdp.pending, (state) => {
        state.isFdpLoading = true;
      })
      .addCase(updateFdp.fulfilled, (state) => {
        state.isFdpLoading = false;
      })
      .addCase(updateFdp.rejected, (state) => {
        state.isFdpLoading = false;
      })
      .addCase(fetchFdpAssociatedClients.pending, (state) => {
        state.isFdpLoading = true;
      })
      .addCase(fetchFdpAssociatedClients.fulfilled, (state, action) => {
        state.isFdpLoading = false;
        if (action.payload.isSuccess) {
          state.fdpAssociatedClients = action.payload.data;
        }
      })
      .addCase(fetchFdpAssociatedClients.rejected, (state, action) => {
        state.isFdpLoading = false;
        state.fdpAssociatedClients = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(getFdpListByClient.pending, (state) => {
        state.isFdpLoading = true;
      })
      .addCase(getFdpListByClient.fulfilled, (state, action) => {
        state.isFdpLoading = false;
        if (action.payload.isSuccess) {
          state.fdpListByClient = action.payload.data;
          const fdpList = action.payload.data.map((client) => (client.companyName));
          if(fdpList.length > 0 && state.selectedFDP === null) {
            state.selectedFDP = fdpList[0];
          }

        } else {
          state.fdpListByClient = [];
        }
      })
      .addCase(getFdpListByClient.rejected, (state, action) => {
        state.isFdpLoading = false;
        state.fdpListByClient = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(addFdpDriverClientEmail.pending, (state) => {
        state.isDriverEmailLoading = true;
      })
      .addCase(addFdpDriverClientEmail.fulfilled, (state, action) => {
        state.isDriverEmailLoading = false;
      })
      .addCase(addFdpDriverClientEmail.rejected, (state, action) => {
        state.isDriverEmailLoading = false;
        state.errorMessage = action.error.message!;
      })
      .addCase(deleteDriverClientEmail.pending, (state) => {
        state.isDriverEmailLoading = true;
      })
      .addCase(deleteDriverClientEmail.fulfilled, (state, action) => {
        state.isDriverEmailLoading = false;
      })
      .addCase(deleteDriverClientEmail.rejected, (state, action) => {
        state.isDriverEmailLoading = false;
        state.errorMessage = action.error.message!;
      })
      .addCase(getfdpDriverClientEmails.fulfilled, (state, action) => {
        state.fdcDriverAllClientEmails = action.payload.data;
      });
  },
});

export default fdpSlice.reducer;
export const {
  clearFdpUserList,
  updateFdpDriverSelectedEmailValue,
  updateFdpAssociatedClients,
  updateGlobalSelectedDriverClientEmail,
  setSelectedFDP
} = fdpSlice.actions;
